import { useEffect, useRef, useState } from "react";
import { Send, X, Loader2, MessageSquare, Minus, ChevronUp } from "lucide-react";
import { useAuth, authFetch } from "@/lib/auth";

const POLL_MS = 4000;

export default function ChatPanel({ listingId, conversationId = null, onClose, otherName, minimized = false, onMinimizeChange }) {
  const { user } = useAuth();
  const [convo, setConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [unread, setUnread] = useState(0);
  const scrollerRef = useRef(null);
  const seenCountRef = useRef(0);
  const minimizedRef = useRef(minimized);
  useEffect(() => { minimizedRef.current = minimized; }, [minimized]);
  const createStartedRef = useRef(false);

  const setMinimized = (v) => onMinimizeChange?.(v);

  const minimize = () => {
    seenCountRef.current = messages.length;
    setUnread(0);
    setMinimized(true);
  };

  // Track unread incoming messages while minimized
  useEffect(() => {
    if (!minimized) {
      seenCountRef.current = messages.length;
      if (unread) setUnread(0);
      return;
    }
    const incoming = messages.slice(seenCountRef.current).filter((m) => m.sender_id !== user?.user_id && !m._pending).length;
    if (incoming !== unread) setUnread(incoming);
    // eslint-disable-next-line
  }, [messages, minimized]);

  // Scroll to bottom when expanding
  useEffect(() => {
    if (!minimized && scrollerRef.current) {
      requestAnimationFrame(() => {
        if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
      });
    }
  }, [minimized]);

  // Initial: open existing conversation directly, or create/get as buyer
  useEffect(() => {
    if (conversationId) {
      setConvo({ conversation_id: conversationId });
      return;
    }
    if (createStartedRef.current) return; // dedupe StrictMode/double-mount fires
    createStartedRef.current = true;
    (async () => {
      try {
        const res = await authFetch(`/api/conversations`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ listing_id: listingId }),
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.detail || "Failed to open chat");
        }
        const c = await res.json();
        setConvo(c);
      } catch (e) { setError(e.message); setLoading(false); }
    })();
  }, [listingId, conversationId]);

  // Poll messages
  useEffect(() => {
    if (!convo) return;
    let dead = false;
    const load = async () => {
      try {
        const res = await authFetch(`/api/conversations/${convo.conversation_id}/messages${minimizedRef.current ? "?peek=1" : ""}`);
        if (!res.ok) return;
        const j = await res.json();
        if (!dead) {
          setMessages(j.messages || []);
          setLoading(false);
          requestAnimationFrame(() => {
            if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
          });
        }
      } catch {}
    };
    load();
    const t = setInterval(load, POLL_MS);
    return () => { dead = true; clearInterval(t); };
  }, [convo]);

  const send = async (e) => {
    e?.preventDefault?.();
    const body = text.trim();
    if (!body || !convo) return;
    setSending(true);
    const optimistic = {
      message_id: `tmp_${Math.random()}`,
      conversation_id: convo.conversation_id,
      sender_id: user?.user_id,
      text: body,
      created_at: new Date().toISOString(),
      _pending: true,
    };
    setMessages((m) => [...m, optimistic]);
    setText("");
    try {
      const res = await authFetch(`/api/conversations/${convo.conversation_id}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: body }),
      });
      if (!res.ok) throw new Error("Send failed");
      const saved = await res.json();
      setMessages((m) => m.map((x) => (x.message_id === optimistic.message_id ? saved : x)));
    } catch (err) {
      setMessages((m) => m.filter((x) => x.message_id !== optimistic.message_id));
      setError(err.message);
    } finally { setSending(false); }
  };

  return (
    <div className="fixed bottom-0 right-0 sm:bottom-5 sm:right-5 z-50 w-full sm:w-[380px] pointer-events-none" data-testid="chat-panel">
      {minimized ? (
        /* Minimized pill bar */
        <div className="pointer-events-auto m-3 sm:m-0 flex items-center gap-2 pl-4 pr-2 py-2.5 rounded-full border border-[#00cccc]/30 shadow-[0_10px_36px_rgba(0,0,0,0.65),0_0_20px_rgba(0,204,204,0.12)]" style={{ background: "#15181C" }}>
          <MessageSquare className="w-4 h-4 text-[#00cccc] shrink-0" />
          <button onClick={() => setMinimized(false)} data-testid="chat-expand" className="flex-1 min-w-0 text-left">
            <span className="block text-sm font-semibold text-white truncate">{otherName || "Seller"}</span>
            <span className="block text-[10px] text-zinc-500 font-mono -mt-0.5">// chat minimized</span>
          </button>
          {unread > 0 && (
            <span data-testid="chat-unread-badge" className="px-2 py-0.5 rounded-full bg-[#00cccc] text-black text-[11px] font-bold shrink-0">{unread}</span>
          )}
          <button onClick={() => setMinimized(false)} data-testid="chat-expand-icon" aria-label="Expand chat"
            className="w-8 h-8 rounded-full hover:bg-white/[0.06] flex items-center justify-center shrink-0"><ChevronUp className="w-4 h-4" /></button>
          <button onClick={onClose} data-testid="chat-close" aria-label="Close chat"
            className="w-8 h-8 rounded-full hover:bg-white/[0.06] flex items-center justify-center shrink-0"><X className="w-4 h-4" /></button>
        </div>
      ) : (
        /* Expanded floating window */
        <div className="pointer-events-auto flex flex-col h-[70vh] sm:h-[540px] rounded-t-2xl sm:rounded-2xl border border-[#00cccc]/30 overflow-hidden shadow-[0_16px_48px_rgba(0,0,0,0.7),0_0_28px_rgba(0,204,204,0.10)]" style={{ background: "#15181C" }}>
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center gap-2 min-w-0">
              <MessageSquare className="w-4 h-4 text-[#00cccc] shrink-0" />
              <div className="min-w-0">
                <div className="text-xs text-zinc-500 font-mono">// Chat with</div>
                <div className="text-sm font-semibold text-white truncate">{otherName || "Seller"}</div>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={minimize} data-testid="chat-minimize" aria-label="Minimize chat"
                className="w-8 h-8 rounded-full hover:bg-white/[0.06] flex items-center justify-center"><Minus className="w-4 h-4" /></button>
              <button onClick={onClose} data-testid="chat-close" aria-label="Close chat"
                className="w-8 h-8 rounded-full hover:bg-white/[0.06] flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
          </div>
          <div ref={scrollerRef} className="flex-1 overflow-y-auto p-4 space-y-2">
            {loading && <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 text-[#00cccc] animate-spin" /></div>}
            {error && <div className="text-xs text-red-400 text-center">{error}</div>}
            {!loading && messages.length === 0 && (
              <div className="text-center text-sm text-zinc-500 py-10">Say hi — ask if the bike is still available, request more photos, or arrange an inspection.</div>
            )}
            {messages.map((m) => {
              const mine = m.sender_id === user?.user_id;
              return (
                <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] px-3.5 py-2 rounded-2xl text-sm ${mine ? "bg-[#00cccc] text-black rounded-br-sm" : "bg-white/[0.04] text-white rounded-bl-sm"} ${m._pending ? "opacity-70" : ""}`}>
                    {m.text}
                  </div>
                </div>
              );
            })}
          </div>
          <form onSubmit={send} className="p-3 border-t border-white/10 flex gap-2">
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…"
              data-testid="chat-input"
              className="flex-1 px-4 py-2.5 bg-[#15181C] border border-white/10 rounded-full text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#00cccc]/50" />
            <button type="submit" disabled={!text.trim() || sending} data-testid="chat-send"
              className="w-11 h-11 rounded-full bg-[#00cccc] text-black flex items-center justify-center disabled:opacity-50 hover:shadow-[0_0_16px_rgba(0,204,204,0.5)]">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
